import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import Cookies from "js-cookie";
import "react-toastify/dist/ReactToastify.css";

import ascendLogo from "../../../assets/ACS-Images/Ascend-Logo.png";
import OnboardingImg from "../../../assets/ACS-Images/Onboarding_Img.jpg";

const CompanyInfo = () => {
  const [formData, setFormData] = useState({
    userEmail: "",
    companyName: "",
    contactEmail: "",
    companyPhone: "",
    companyFax: "",
    addressLine1: "",
    addressLine2: "",
    city: "",          
    state: "",         
    zipCode: "",
    country: "",       
    department: "",
    countryCode: "",
    countryName: "",
    stateCode: "",
    stateName: "",
    cityCode: "",
    cityName: "",
  });

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false); // company profile

  // --- Country typeahead ---
  const [countryQuery, setCountryQuery] = useState("");
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryOpen, setCountryOpen] = useState(false);
  const [countryLoading, setCountryLoading] = useState(false);
  const [countryActiveIdx, setCountryActiveIdx] = useState(-1);
  const countryAbortRef = useRef(null);
  const countryListRef = useRef(null);

  // --- State typeahead (depends on countryCode) ---
  const [stateQuery, setStateQuery] = useState("");
  const [stateOptions, setStateOptions] = useState([]);
  const [stateOpen, setStateOpen] = useState(false);
  const [stateLoading, setStateLoading] = useState(false);
  const [stateActiveIdx, setStateActiveIdx] = useState(-1);
  const stateAbortRef = useRef(null);
  const stateListRef = useRef(null);

  // --- City typeahead (depends on countryCode + stateCode) ---
  const [cityQuery, setCityQuery] = useState("");
  const [cityOptions, setCityOptions] = useState([]);
  const [cityOpen, setCityOpen] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [cityActiveIdx, setCityActiveIdx] = useState(-1);
  const cityAbortRef = useRef(null);
  const cityListRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  // ---------------- Country helpers ----------------
  const normalizeCountries = (raw) => {
    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return list
      .map((item) => {
        if (!item) return null;
        if (typeof item === "string") return { code: item, name: item };
        return {
          code:
            item.code ||
            item.isoCode ||
            item.countryCode ||
            item.alpha2 ||
            item.alpha3 ||
            item.name ||
            item.countryName,
          name: item.name || item.countryName || item.label || item.code,
        };
      })
      .filter(Boolean)
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  };

  const searchCountries = async (query) => {
    if (countryAbortRef.current) countryAbortRef.current.abort();
    const controller = new AbortController();
    countryAbortRef.current = controller;

    try {
      setCountryLoading(true);
      const token = Cookies.get("authToken");
      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL || ""}/api/search/countries`,
        {
          params: { name: query }, // backend expects ?name=
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        }
      );
      const normalized = normalizeCountries(resp.data);
      setCountryOptions(normalized);
      setCountryOpen(true);
      setCountryActiveIdx(normalized.length ? 0 : -1);
    } catch (err) {
      if (axios.isCancel?.(err) || err.name === "CanceledError") return;
      console.error("Country search failed", err);
      setCountryOptions([]);
      setCountryOpen(true);
      setCountryActiveIdx(-1);
    } finally {
      setCountryLoading(false);
    }
  };

  // Debounced country search as user types (no Enter needed)
  useEffect(() => {
    const q = (countryQuery || formData.countryName || "").trim();
    if (q.length < 2) {
      setCountryOptions([]);
      setCountryOpen(false);
      return;
    }
    const id = setTimeout(() => {
      searchCountries(q);
    }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryQuery]);

  // Close country suggestions on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!countryListRef.current) return;
      if (!countryListRef.current.contains(e.target)) setCountryOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Hide typed country text as an option
  const normalizedCountryQuery = (countryQuery || formData.countryName || "")
    .trim()
    .toLowerCase();

 const visibleCountryOptions = useMemo(() => {
  const seen = new Set();
  return countryOptions.filter((o) => {
    const name = (o.name || "").toLowerCase();
    const code = (o.code || "").toLowerCase();
    const key = `${name}::${code}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}, [countryOptions]);


  const handleCountrySelect = (opt) => {
    const code = opt?.code || "";
    const name = opt?.name || code;
    // update display & payload
    setFormData((p) => ({
      ...p,
      country: code || name,
      countryCode: code,
      countryName: name,
      // reset state & city when country changes
      state: "",
      stateCode: "",
      stateName: "",
      city: "",
      cityCode: "",
      cityName: "",
    }));
    setCountryQuery(name);
    setCountryQuery("");
    
    setCountryOpen(false);
    setCountryActiveIdx(-1);
    // clear state + city queries/options
    setStateQuery("");
    setStateOptions([]);
    setStateOpen(false);
    setStateActiveIdx(-1);

    setCityQuery("");
    setCityOptions([]);
    setCityOpen(false);
    setCityActiveIdx(-1);
  };

  // ---------------- State helpers ----------------
  const normalizeStates = (raw) => {
    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return list
      .map((item) => {
        if (!item) return null;
        if (typeof item === "string") return { code: item, name: item };
        return {
          code:
            item.code ||
            item.stateCode ||
            item.isoCode ||
            item.name ||
            item.stateName,
          name: item.name || item.stateName || item.label || item.code,
        };
      })
      .filter(Boolean)
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  };

  const searchStates = async (query, countryCode) => {
    if (!countryCode) return;
    if (stateAbortRef.current) stateAbortRef.current.abort();
    const controller = new AbortController();
    stateAbortRef.current = controller;

    try {
      setStateLoading(true);
      const token = Cookies.get("authToken");
      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL || ""}/api/search/states/by-country`,
        {
          params: { countryCode, stateName: query },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        }
      );
      const normalized = normalizeStates(resp.data);
      setStateOptions(normalized);
      setStateOpen(true);
      setStateActiveIdx(normalized.length ? 0 : -1);
    } catch (err) {
      if (axios.isCancel?.(err) || err.name === "CanceledError") return;
      console.error("State search failed", err);
      setStateOptions([]);
      setStateOpen(true);
      setStateActiveIdx(-1);
    } finally {
      setStateLoading(false);
    }
  };

  // Debounced state search (needs countryCode)
  useEffect(() => {
    const q = (stateQuery || formData.stateName || "").trim();
    if (q.length < 2) {
      setStateOptions([]);
      setStateOpen(false);
      return;
    }
    if (!formData.countryCode) return;
    const id = setTimeout(() => {
      searchStates(q, formData.countryCode);
    }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateQuery, formData.countryCode]);

  // Close state suggestions on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!stateListRef.current) return;
      if (!stateListRef.current.contains(e.target)) setStateOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Hide typed state text as an option
  const normalizedStateQuery = (stateQuery || formData.stateName || "")
    .trim()
    .toLowerCase();

  const visibleStateOptions = useMemo(() => {
  const seen = new Set();
  return stateOptions.filter((o) => {
    const name = (o.name || "").toLowerCase();
    const code = (o.code || "").toLowerCase();
    const key = `${name}::${code}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}, [stateOptions]);


  const handleStateSelect = (opt) => {
    const code = opt?.code || "";
    const name = opt?.name || code;
    setFormData((p) => ({
      ...p,
      state: code || name,
      stateCode: code,
      stateName: name,
      // reset city when state changes
      city: "",
      cityCode: "",
      cityName: "",
    }));
    setStateQuery(name);
    setStateOpen(false);
    setStateActiveIdx(-1);

    // clear city selections/options
    setCityQuery("");
    setCityOptions([]);
    setCityOpen(false);
    setCityActiveIdx(-1);
  };

  // ---------------- City helpers ----------------
  const normalizeCities = (raw) => {
    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return list
      .map((item) => {
        if (!item) return null;
        if (typeof item === "string") return { code: item, name: item };
        return {
          code:
            item.code ||
            item.cityCode ||
            item.isoCode ||
            item.name ||
            item.cityName,
          name: item.name || item.cityName || item.label || item.code,
        };
      })
      .filter(Boolean)
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  };

  const searchCities = async (query, countryCode, stateCode) => {
    if (!countryCode || !stateCode) return;
    if (cityAbortRef.current) cityAbortRef.current.abort();
    const controller = new AbortController();
    cityAbortRef.current = controller;

    try {
      setCityLoading(true);
      const token = Cookies.get("authToken");
      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL || ""}/api/search/cities/by-country-state`,
        {
          params: { countryCode, stateCode, cityName: query },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        }
      );
      const normalized = normalizeCities(resp.data);
      setCityOptions(normalized);
      setCityOpen(true);
      setCityActiveIdx(normalized.length ? 0 : -1);
    } catch (err) {
      if (axios.isCancel?.(err) || err.name === "CanceledError") return;
      console.error("City search failed", err);
      setCityOptions([]);
      setCityOpen(true);
      setCityActiveIdx(-1);
    } finally {
      setCityLoading(false);
    }
  };

  // Debounced city search (needs countryCode + stateCode)
  useEffect(() => {
    const q = (cityQuery || formData.cityName || "").trim();
    if (q.length < 1) {
      setCityOptions([]);
      setCityOpen(false);
      return;
    }
    if (!formData.countryCode || !formData.stateCode) return;
    const id = setTimeout(() => {
      searchCities(q, formData.countryCode, formData.stateCode);
    }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityQuery, formData.countryCode, formData.stateCode]);

  // Close city suggestions on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!cityListRef.current) return;
      if (!cityListRef.current.contains(e.target)) setCityOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Hide typed city text as an option
  const normalizedCityQuery = (cityQuery || formData.cityName || "")
    .trim()
    .toLowerCase();

 const visibleCityOptions = useMemo(() => {
  const seen = new Set();
  return cityOptions.filter((o) => {
    const name = (o.name || "").toLowerCase();
    const code = (o.code || "").toLowerCase();
    const key = `${name}::${code}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}, [cityOptions]);
const saveNewCity = async (cityName) => {
  if (!formData.countryCode || !formData.stateCode || !cityName.trim()) return;
  
  const exactMatch = cityOptions.find(
    (o) => (o.name || "").toLowerCase() === cityName.toLowerCase()
  );
  if (exactMatch) return;

  try {
    const token = Cookies.get("authToken");
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL || ""}/api/search/cities/save-new`,
      null,
      {
        params: {
          countryCode: formData.countryCode,
          stateCode: formData.stateCode,
          cityName: cityName.trim(),
        },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    if (response.data) {
      setFormData((p) => ({
        ...p,
        city: response.data.cityName,
        cityName: response.data.cityName,
      }));
    }
  } catch (err) {
    console.error("Failed to save new city", err);
  }
};

  const handleCitySelect = (opt) => {
    const code = opt?.code || "";
    const name = opt?.name || code;
    setFormData((p) => ({
      ...p,
      city: code || name, // submit code/value
      cityCode: code,
      cityName: name,
    }));
    setCityQuery(name);
    setCityOpen(false);
    setCityActiveIdx(-1);
  };

  // ---------------- Existing logic ----------------
  useEffect(() => {
    if (location.state?.companyName && location.state?.email) {
      setFormData((prevData) => ({
        ...prevData,
        companyName: location.state.companyName,
        userEmail: location.state.email,
      }));
      fetchCompanyProfile(location.state.companyName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.companyName, location.state?.email]);

  const fetchCompanyProfile = async (companyName) => {
    if (!companyName) return;
    setFetchingData(true);
    try {
      const token = Cookies.get("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/onboarding/company-profile`,
        {
          params: { companyName },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      if (response.status === 200 && response.data) {
        setFormData((prevData) => ({
          ...prevData,
          ...response.data,
          userEmail: location.state.email,
          companyName: location.state.companyName,
        }));
        
      }
    } catch (_err) {
      // ignore if not found
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const required = [
      "contactEmail",
      "companyPhone",
      "addressLine1",
      "city",
      "state",
      "country",
      "department",
    ];
    const missing = required.filter((f) => !formData[f]);
    if (missing.length) {
      toast.error(`Please fill in all required fields: ${missing.join(", ")}`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const token = Cookies.get("authToken");
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/onboarding/company-profile`,
        {
          companyName: formData.companyName,
          contactEmail: formData.contactEmail,
          phoneNumber: formData.companyPhone,
          faxNumber: formData.companyFax,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          state: formData.state,   // selected state code/value
          city: formData.city,     // selected city code/value
          zipCode: formData.zipCode,
          country: formData.country, // selected country code/value
          department: formData.department,
        },
        {
          params: { userEmail: formData.userEmail },
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (response.status === 200) {
        navigate("/onboarding/final-onboard");
      }
    } catch (error) {
      const msg =
        error.response?.data?.message || "Failed to save company profile";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };


  
  return (
    <div className="w-full min-h-screen bg-white">
      <ToastContainer />

      <div className="grid grid-cols-1 md:grid-cols-12 min-h-screen">
        {/* Left Picture (25%) */}
        <div
          className="hidden md:block md:col-span-2 h-screen bg-cover bg-center sticky top-0"
          style={{ backgroundImage: `url(${OnboardingImg})` }}
          aria-hidden="true"
        />

        {/* Center Form (50%) */}
        <div className="md:col-span-8 min-h-screen flex items-start md:items-center justify-center py-8 md:py-12">
          {fetchingData ? (
            <div className="text-center">
              <p>Loading company information...</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="w-full max-w-7xl px-4 md:px-10 py-4 md:py-10 space-y-fluid-3"
            >
              {/* Logo + Stepper */}
              <div className="w-full max-w-5xl px-0 md:px-8 py-0 md:py-6 mx-auto">
                <div className="flex flex-col items-center space-y-4">
                  <img
                    src={ascendLogo}
                    alt="Ascend Logo"
                    className="w-32 md:w-40 mb-6 hover:scale-105 transition-transform duration-300"
                  />
                  <div className="flex items-center justify-center gap-6 mt-2 mb-4">
                    {["About yourself", "Company Information", "Invite your team"].map((label, idx) => {
                      const isActive = idx === 1;
                      const isCompleted = idx < 1;
                      return (
                        <React.Fragment key={idx}>
                          {idx > 0 && <div className="w-8 border-t-2 border-gray-300" />}
                          <div className="flex flex-col items-center">
                            <div
                              className={`w-8 h-8 rounded-full font-semibold flex items-center justify-center
                                ${isCompleted ? "bg-[#223688] text-white border-2 border-[#223688]" : ""}
                                ${isActive ? "border-2 border-[#223688] bg-[#e8edff] text-[#223688]" : ""}
                                ${!isActive && !isCompleted ? "border border-gray-300 bg-white text-gray-400" : ""}`}
                            >
                              {idx + 1}
                            </div>
                            <span
                              className={`mt-1 text-sm font-semibold text-center
                                ${isActive ? "text-black" : ""}
                                ${!isActive && !isCompleted ? "text-gray-400" : ""}
                                ${isCompleted ? "text-[#223688]" : ""}`}
                            >
                              {label}
                            </span>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="text-center mb-fluid-4">
                <h1 className="text-fluid-xl font-bold">Tell us about your company</h1>
                <p className="text-gray-600 mt-2">{formData.companyName}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-fluid-2">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Company contact email <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    name="contactEmail"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    value={formData.contactEmail}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Company phone number <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="tel"
                    name="companyPhone"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    value={formData.companyPhone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Company fax number</label>
                  <input
                    type="tel"
                    name="companyFax"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    value={formData.companyFax}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Company address line 1 <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="addressLine1"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    value={formData.addressLine1}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Company address line 2</label>
                  <input
                    type="text"
                    name="addressLine2"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    value={formData.addressLine2}
                    onChange={handleChange}
                  />
                </div>

                 {/* Country */}
                <div className="relative" ref={countryListRef}>
                  <label className="block text-sm font-medium mb-1">
                    Country <span className="text-red-600">*</span>
                  </label>

                  <input
                    type="text"
                    name="countryName"
                    autoComplete="off"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    value={countryQuery || formData.countryName}
                    placeholder="Start typing a country"
                    onFocus={() =>
                      setCountryOpen((visibleCountryOptions?.length ?? 0) > 0)
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      setCountryQuery(val);
                      setFormData((p) => ({ ...p, countryName: val }));
                      setCountryOpen(true);
                      setCountryActiveIdx(-1);
                    }}
                    onKeyDown={(e) => {
                      if (!countryOpen) return;
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setCountryActiveIdx((i) =>
                          Math.min((visibleCountryOptions?.length ?? 1) - 1, i + 1)
                        );
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setCountryActiveIdx((i) => Math.max(0, i - 1));
                      } else if (e.key === "Enter") {
                        if (
                          countryActiveIdx >= 0 &&
                          countryActiveIdx < (visibleCountryOptions?.length ?? 0)
                        ) {
                          e.preventDefault();
                          handleCountrySelect(visibleCountryOptions[countryActiveIdx]);
                        }
                      } else if (e.key === "Escape" || e.key === "Tab") {
                        setCountryOpen(false);
                      }
                    }}
                    required
                  />

                  {countryOpen &&
                    (countryLoading || visibleCountryOptions.length > 0) && (
                      <ul
                        role="listbox"
                        className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
                      >
                        {countryLoading && (
                          <li className="px-4 py-2 text-sm text-gray-500">
                            Searching…
                          </li>
                        )}

                        {!countryLoading &&
                          visibleCountryOptions.map((opt, idx) => {
                            const active = idx === countryActiveIdx;
                            return (
                              <li
                                key={`${opt.code}-${opt.name}-${idx}`}
                                role="option"
                                aria-selected={active}
                                className={`px-4 py-2 cursor-pointer ${
                                  active
                                    ? "bg-[#e8edff] text-[#223688]"
                                    : "hover:bg-gray-50"
                                }`}
                                onMouseEnter={() => setCountryActiveIdx(idx)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleCountrySelect(opt)}
                              >
                                <div className="font-medium">{opt.name}</div>
                                {opt.code && opt.code !== opt.name && (
                                  <div className="text-xs text-gray-500">
                                    {opt.code}
                                  </div>
                                )}
                              </li>
                            );
                          })}

                        {!countryLoading &&
                          visibleCountryOptions.length === 0 &&
                          normalizedCountryQuery.length >= 3 && (
                            <li className="px-4 py-2 text-sm text-gray-500">
                              No matches
                            </li>
                          )}
                      </ul>
                    )}
                </div>

                {/* State (depends on country) */}
                <div className="relative" ref={stateListRef}>
                  <label className="block text-sm font-medium mb-1">
                    State <span className="text-red-600">*</span>
                  </label>

                  <input
                    type="text"
                    name="stateName"
                    autoComplete="off"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    value={stateQuery || formData.stateName}
                    disabled={!formData.countryCode}
                    onFocus={() => setStateOpen((visibleStateOptions?.length ?? 0) > 0)}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!formData.countryCode) {
                        toast.info("Please select a country first.");
                        return;
                      }
                      setStateQuery(val);
                      setFormData((p) => ({ ...p, stateName: val }));
                      setStateOpen(true);
                      setStateActiveIdx(-1);
                    }}
                    onKeyDown={(e) => {
                      if (!stateOpen) return;
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setStateActiveIdx((i) =>
                          Math.min((visibleStateOptions?.length ?? 1) - 1, i + 1)
                        );
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setStateActiveIdx((i) => Math.max(0, i - 1));
                      } else if (e.key === "Enter") {
                        if (
                          stateActiveIdx >= 0 &&
                          stateActiveIdx < (visibleStateOptions?.length ?? 0)
                        ) {
                          e.preventDefault();
                          handleStateSelect(visibleStateOptions[stateActiveIdx]);
                        }
                      } else if (e.key === "Escape" || e.key === "Tab") {
                        setStateOpen(false);
                      }
                    }}
                    required
                  />



                  {stateOpen && (stateLoading || visibleStateOptions.length > 0) && (
                    <ul
                      role="listbox"
                      className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
                    >
                      {stateLoading && (
                        <li className="px-4 py-2 text-sm text-gray-500">Searching…</li>
                      )}

                      {!stateLoading &&
                        visibleStateOptions.map((opt, idx) => {
                          const active = idx === stateActiveIdx;
                          return (
                            <li
                              key={`${opt.code}-${opt.name}-${idx}`}
                              role="option"
                              aria-selected={active}
                              className={`px-4 py-2 cursor-pointer ${
                                active ? "bg-[#e8edff] text-[#223688]" : "hover:bg-gray-50"
                              }`}
                              onMouseEnter={() => setStateActiveIdx(idx)}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleStateSelect(opt)}
                            >
                              <div className="font-medium">{opt.name}</div>
                              {opt.code && opt.code !== opt.name && (
                                <div className="text-xs text-gray-500">{opt.code}</div>
                              )}
                            </li>
                          );
                        })}

                      {!stateLoading &&
                        visibleStateOptions.length === 0 &&
                        normalizedStateQuery.length >= 3 && (
                          <li className="px-4 py-2 text-sm text-gray-500">No matches</li>
                        )}
                    </ul>
                  )}
                </div>

                {/* City (depends on country + state) */}
                <div className="relative" ref={cityListRef}>
                  <label className="block text-sm font-medium mb-1">
                    City <span className="text-red-600">*</span>
                  </label>

                  <input
                    type="text"
                    name="cityName"
                    autoComplete="off"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    value={cityQuery || formData.cityName}
                    disabled={!formData.countryCode || !formData.stateCode}
                    onFocus={() => setCityOpen((visibleCityOptions?.length ?? 0) > 0)}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!formData.countryCode || !formData.stateCode) {
                        toast.info("Please select country and state first.");
                        return;
                      }
                      setCityQuery(val);
                      setFormData((p) => ({ ...p, cityName: val,city: val }));
                      setCityOpen(true);
                      setCityActiveIdx(-1);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (
                          cityActiveIdx >= 0 &&
                          cityActiveIdx < (visibleCityOptions?.length ?? 0)
                        ) {
                          e.preventDefault();
                          handleCitySelect(visibleCityOptions[cityActiveIdx]);
                        }
                         else if (cityQuery.trim().length > 0) {
                          e.preventDefault();
                          saveNewCity(cityQuery.trim());
                          setCityOpen(false);
                        }
                      } else if (!cityOpen) return;
                      else if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setCityActiveIdx((i) =>
                        Math.min((visibleCityOptions?.length ?? 1) - 1, i + 1)
                      );
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setCityActiveIdx((i) => Math.max(0, i - 1));
                    } else if (e.key === "Escape" || e.key === "Tab") {
                      setCityOpen(false);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      if (cityQuery.trim().length > 0) {
                        saveNewCity(cityQuery.trim());
                      }
                      setCityOpen(false);
                    }, 200);
                  }}
                  required
                  />

                  {cityOpen && (cityLoading || visibleCityOptions.length > 0) && (
                    <ul
                      role="listbox"
                      className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
                    >
                      {cityLoading && (
                        <li className="px-4 py-2 text-sm text-gray-500">Searching…</li>
                      )}

                      {!cityLoading &&
                        visibleCityOptions.map((opt, idx) => {
                          const active = idx === cityActiveIdx;
                          return (
                            <li
                              key={`${opt.code}-${opt.name}-${idx}`}
                              role="option"
                              aria-selected={active}
                              className={`px-4 py-2 cursor-pointer ${
                                active ? "bg-[#e8edff] text-[#223688]" : "hover:bg-gray-50"
                              }`}
                              onMouseEnter={() => setCityActiveIdx(idx)}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleCitySelect(opt)}
                            >
                              <div className="font-medium">{opt.name}</div>
                              {opt.code && opt.code !== opt.name && (
                                <div className="text-xs text-gray-500">{opt.code}</div>
                              )}
                            </li>
                          );
                        })}

                      {!cityLoading &&
                        visibleCityOptions.length === 0 &&
                        normalizedCityQuery.length >= 3 && (
                          <li className="px-4 py-2 text-sm text-gray-500">No matches</li>
                        )}
                    </ul>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Zip code/Pin<span className="text-red-600">*</span></label>
                  <input
                    type="text"
                    name="zipCode"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    value={formData.zipCode}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Your department <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="department"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    value={formData.department}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a department type</option>
                    <option value="Logistics">Logistics</option>
                    <option value="Procurement">Procurement</option>
                    <option value="IT">IT</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-center gap-6 mt-fluid-4">
                <button
                  type="submit"
                  className="w-40 px-6 py-2 bg-[#223688] text-white rounded-full hover:bg-[#1c2c6d] transition"
                  disabled={loading}
                >
                  {loading ? "Processing.." : "Continue"}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Right Picture (25%) */}
        <div
          className="hidden md:block md:col-span-2 h-screen bg-cover bg-center sticky top-0"
          style={{ backgroundImage: `url(${OnboardingImg})` }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

export default CompanyInfo;
